import { router, usePage } from "@inertiajs/react"
import { type PropsWithChildren, useEffect, useRef } from "react"

import { AppHeader } from "@/components/app-header"
import MobileBottomNav from "@/components/mobile-bottom-nav"
import MobileHeader from "@/components/mobile-header"
import { useAuth } from "@/hooks/use-auth"
import type { BreadcrumbItem } from "@/types"

interface AppMobileLayoutProps {
  breadcrumbs?: BreadcrumbItem[]
  title?: string
  showMobileNav?: boolean
  // MobileHeader props
  showBackButton?: boolean
  showShareButton?: boolean
  showFavoriteButton?: boolean
  showEditButton?: boolean
  showDeleteButton?: boolean
  onBack?: () => void
  onShare?: () => void
  onFavorite?: (isFavorite: boolean) => void
  onEdit?: () => void
  onDelete?: () => void
  onCreateChat?: () => void
  isFavorite?: boolean
  favoritesCount?: number
  headerChildren?: React.ReactNode
}

export default function AppMobileLayout({
  children,
  breadcrumbs = [],
  title,
  showMobileNav = true,
  // MobileHeader props
  showBackButton = false,
  showShareButton = false,
  showFavoriteButton = false,
  showEditButton = false,
  showDeleteButton = false,
  onBack,
  onShare,
  onFavorite,
  onEdit,
  onDelete,
  onCreateChat,
  isFavorite = false,
  favoritesCount,
  headerChildren,
}: PropsWithChildren<AppMobileLayoutProps>) {
  const { user } = useAuth()
  const mainRef = useRef<HTMLElement>(null)
  const { url } = usePage()

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0
    }
  }, [url])

  const handleResendVerification = () => {
    router.post(
      "/identity/email_verification",
      {},
      { preserveScroll: true, preserveState: true },
    )
  }
  return (
    <>
      {/* Desktop: Use header layout without sidebar */}
      <div className="hidden md:flex md:min-h-screen md:flex-col">
        <AppHeader breadcrumbs={breadcrumbs} />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-6">
            {user && user.verified === false && (
              <div className="mb-4 flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <span>
                  Tu email no está verificado. Verifica tu cuenta para crear
                  contenido, participar en sorteos o asistir a eventos.
                </span>
                <button
                  onClick={handleResendVerification}
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                >
                  Reenviar verificación
                </button>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>

      {/* Mobile: Native app-like layout */}
      <div className="bg-background flex h-dvh flex-col overflow-hidden md:hidden">
        {/* Fixed mobile header */}
        {showMobileNav && (
          <MobileHeader
            title={title}
            showBackButton={showBackButton}
            showShareButton={showShareButton}
            showFavoriteButton={showFavoriteButton}
            showEditButton={showEditButton}
            showDeleteButton={showDeleteButton}
            onBack={onBack}
            onShare={onShare}
            onFavorite={onFavorite}
            onEdit={onEdit}
            onDelete={onDelete}
            onCreateChat={onCreateChat}
            isFavorite={isFavorite}
            favoritesCount={favoritesCount}
          >
            {headerChildren}
          </MobileHeader>
        )}

        {/* Main content - scrollable area between header and bottom nav */}
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto overscroll-contain pt-1"
        >
          <div className={showMobileNav ? "pb-safe-nav" : "pb-safe"}>
            {user && user.verified === false && (
              <div className="m-4 flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <span>
                  Tu email no está verificado. Verifica tu cuenta para crear
                  contenido, participar en sorteos o asistir a eventos.
                </span>
                <button
                  onClick={handleResendVerification}
                  className="ml-3 inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                >
                  Verificar
                </button>
              </div>
            )}
            {children}
          </div>
        </main>

        {/* Fixed bottom navigation */}
        {showMobileNav && <MobileBottomNav />}
      </div>
    </>
  )
}
